import { useEffect, useState, useRef } from 'react';
import { supabase } from '@product-radar/shared';
import { PendingInvitation, useInvitations } from '@/contexts/context';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { MarketingPanel } from './marketing-panel';
import productRadarLogo from '@/assets/product_radar_logo_png_blue.png';

interface AcceptInvitationProps {
  onAccepted: () => void;
  onError: () => void;
}

interface WorkspaceMembership {
  id: string;
  workspace_id: string;
  role: string;
  status: 'invited' | 'active';
  expires_at?: string | null;
}

// ---------------------------------------------------------------------------
// localStorage helpers – persist invitation context across OAuth redirects
// ---------------------------------------------------------------------------
const PENDING_OAUTH_INVITE_KEY = 'pr_pending_oauth_invite';

interface PendingOAuthInvite {
  membershipId: string;
  workspaceName: string;
  timestamp: number;
}

const savePendingOAuthInvite = (membershipId: string, workspaceName: string) => {
  localStorage.setItem(
    PENDING_OAUTH_INVITE_KEY,
    JSON.stringify({ membershipId, workspaceName, timestamp: Date.now() } satisfies PendingOAuthInvite),
  );
};

export const getPendingOAuthInvite = (): PendingOAuthInvite | null => {
  try {
    const raw = localStorage.getItem(PENDING_OAUTH_INVITE_KEY);
    if (!raw) return null;
    const data: PendingOAuthInvite = JSON.parse(raw);
    // Expire after 10 minutes to avoid stale state
    if (Date.now() - data.timestamp > 10 * 60 * 1000) {
      localStorage.removeItem(PENDING_OAUTH_INVITE_KEY);
      return null;
    }
    return data;
  } catch {
    localStorage.removeItem(PENDING_OAUTH_INVITE_KEY);
    return null;
  }
};

export const clearPendingOAuthInvite = () => {
  localStorage.removeItem(PENDING_OAUTH_INVITE_KEY);
};

// Check if invitation is expired
const isInvitationExpired = (membership: WorkspaceMembership): boolean => {
  if (!membership.expires_at) {
    return false; // No expiration set
  }
  return new Date(membership.expires_at) < new Date();
};

// Fetch a specific membership
const fetchMembership = async (workspaceId: string, userId: string) => {
  const { data, error } = await supabase
    .from('workspace_members')
    .select('id, workspace_id, role, status, expires_at')
    .eq('workspace_id', workspaceId)
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('Failed to read workspace membership:', error);
    return null;
  }

  return data as WorkspaceMembership | null;
};

const updateLastActive = async (workspaceId: string, userId: string) => {
  try {
    await supabase.rpc('update_last_active_workspace', {
      p_workspace_id: workspaceId,
      p_user_id: userId,
    });
  } catch (err) {
    console.warn('Failed to update last active workspace:', err);
  }
};

const activateMembership = async (membershipId: string) => {
  const { data, error } = await supabase.rpc('accept_workspace_invitation', {
    p_membership_id: membershipId,
  });

  if (error) {
    console.error('Failed to activate membership:', error);
    if (error.message?.includes('INVITATION_EXPIRED')) {
      throw new Error('INVITATION_EXPIRED');
    }
    return null;
  }

  return data as WorkspaceMembership;
};

export function AcceptInvitation({ onAccepted, onError }: AcceptInvitationProps) {
  const { pendingInvitations: invitationCache, refreshInvitations } = useInvitations();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>(invitationCache);
  const [selectedInvitation, setSelectedInvitation] = useState<PendingInvitation | null>(null);
  const [membership, setMembership] = useState<WorkspaceMembership | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // OAuth state
  const [autoAcceptPending, setAutoAcceptPending] = useState(false);
  const pendingOAuthRef = useRef(getPendingOAuthInvite());
  
  // Use ref to prevent infinite loop
  const initializedRef = useRef(false);

  useEffect(() => {
    setPendingInvitations(invitationCache);
  }, [invitationCache]);

  // Auto-accept after OAuth return
  useEffect(() => {
    if (!autoAcceptPending || !membership || accepting || loading) return;
    setAutoAcceptPending(false);
    clearPendingOAuthInvite();
    // Accept without setting a password – the user authenticated via OAuth
    completeAccess(membership);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAcceptPending, membership, accepting, loading]);

  useEffect(() => {
    // Prevent infinite loop by only running once
    if (initializedRef.current) {
      console.log('[AcceptInvitation] already initialized, skipping');
      return;
    }
    
    const initialize = async () => {
      console.log('[AcceptInvitation] initialize start');
      try {
        setLoading(true);
        console.log('[AcceptInvitation] loading set to true');

        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();

        console.log('[AcceptInvitation] got user', user?.id);

        if (userError) {
          console.error('[AcceptInvitation] user error', userError);
          throw userError;
        }

        if (!user) {
          console.log('[AcceptInvitation] no user, showing error');
          setError('Please follow the invitation link in your email to continue.');
          onError();
          return;
        }

        // Refresh invitations
        console.log('[AcceptInvitation] refreshing invitations');
        let invitations: PendingInvitation[] = [];
        const refreshedInvites = await refreshInvitations();
        invitations = refreshedInvites;
        console.log('[AcceptInvitation] got invitations', invitations.length);

        if (invitations.length === 0) {
          // If we were returning from an OAuth redirect, the identity may not have linked
          if (pendingOAuthRef.current) {
            clearPendingOAuthInvite();
            console.log('[AcceptInvitation] OAuth return but no invitations found');
            setError(
              'Could not find your invitation. This may happen if the email on your Google/Microsoft account ' +
              "doesn't match the invited email address. Please use the original invite link or ask your administrator to resend.",
            );
          } else {
            console.log('[AcceptInvitation] no invitations found');
            setError('No pending invitations found. Please ask your administrator to resend the invite.');
          }
          onError();
          return;
        }

        // Filter out expired invitations
        const validInvitations = invitations.filter((inv) => {
          if (!inv.membership.expires_at) {
            return true; // No expiration set
          }
          return new Date(inv.membership.expires_at) >= new Date();
        });
        console.log('[AcceptInvitation] valid invitations', validInvitations.length);

        if (validInvitations.length === 0) {
          console.log('[AcceptInvitation] all invitations expired');
          setError('All invitations have expired. Please ask your administrator to send a new invitation.');
          onError();
          return;
        }

        setPendingInvitations(validInvitations);
        console.log('[AcceptInvitation] set pending invitations');

        // Check if returning from an OAuth redirect (pending invite in localStorage)
        const pendingOAuth = pendingOAuthRef.current;
        if (pendingOAuth) {
          console.log('[AcceptInvitation] detected OAuth return, looking for membership', pendingOAuth.membershipId);
          const matchingInvite = validInvitations.find(
            (inv) => inv.membership.id === pendingOAuth.membershipId,
          );
          if (matchingInvite) {
            const membershipData = matchingInvite.membership as WorkspaceMembership;
            if (!isInvitationExpired(membershipData)) {
              console.log('[AcceptInvitation] OAuth return – auto-accepting');
              setSelectedInvitation(matchingInvite);
              setMembership(membershipData);
              setAutoAcceptPending(true);
              return; // auto-accept effect will handle the rest
            }
          }
          // Matching invitation not found or expired — fall through to normal flow
          clearPendingOAuthInvite();
        }

        // If only one invitation, auto-select it (but do not auto-accept yet;
        // the user will set a password / use OAuth and explicitly accept).
        if (validInvitations.length === 1) {
          const invitation = validInvitations[0];
          console.log('[AcceptInvitation] auto-selecting single invitation');
          setSelectedInvitation(invitation);
          setMembership(invitation.membership as WorkspaceMembership);

          // Check expiration before allowing acceptance
          if (isInvitationExpired(invitation.membership as WorkspaceMembership)) {
            console.log('[AcceptInvitation] invitation expired');
            setError('This invitation has expired. Please ask your administrator to send a new invitation.');
            onError();
            return;
          }
        }
        console.log('[AcceptInvitation] initialization complete');
        // If multiple invitations, user will select one before accepting
      } catch (err) {
        console.error('[AcceptInvitation] Failed to initialize invitation flow:', err);
        setError('We could not verify this invitation. Please try again or request a new invite.');
        onError();
      } finally {
        console.log('[AcceptInvitation] setting loading to false');
        setLoading(false);
        initializedRef.current = true;
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const completeAccess = async (currentMembership: WorkspaceMembership, newPassword?: string) => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      setError('User session not found. Please sign in again.');
      return;
    }

    // If a new password is provided, update it before accepting the invitation
    if (newPassword && newPassword.length > 0) {
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) {
        console.error('Failed to set password during invitation acceptance:', updateError);
        setError(updateError.message || 'Failed to set password. Please try again.');
        return;
      }
    }

    // Check expiration again before accepting
    if (isInvitationExpired(currentMembership)) {
      setError('This invitation has expired. Please ask your administrator to send a new invitation.');
      onError();
      return;
    }

    try {
      setAccepting(true);

      let resolvedMembership = currentMembership;

      if (currentMembership.status === 'invited') {
        const activated = await activateMembership(currentMembership.id);
        if (!activated) {
          setError('Failed to activate workspace membership. Please try again shortly.');
          return;
        }
        resolvedMembership = activated;
        setMembership(activated);
      }

      await updateLastActive(resolvedMembership.workspace_id, user.id);
      await refreshInvitations();

      const workspaceName = selectedInvitation?.workspaceName || 'your workspace';
      toast.success(`You're all set for ${workspaceName}!`);
      onAccepted();
    } catch (err: any) {
      console.error('Failed to accept invitation:', err);
      if (err.message === 'INVITATION_EXPIRED') {
        setError('This invitation has expired. Please ask your administrator to send a new invitation.');
      } else {
        setError('Failed to accept invitation. Please try again.');
      }
      onError();
    } finally {
      setAccepting(false);
    }
  };

  const handleSelectInvitation = async (invitation: PendingInvitation) => {
    const membershipData = invitation.membership as WorkspaceMembership;
    
    // Check expiration before selecting
    if (isInvitationExpired(membershipData)) {
      toast.error('This invitation has expired. Please ask your administrator to send a new invitation.');
      return;
    }

    setSelectedInvitation(invitation);
    setMembership(membershipData);
  };

  // ---------------------------------------------------------------------------
  // OAuth handler – saves invite state to localStorage then redirects
  // ---------------------------------------------------------------------------
  const handleOAuthSignIn = async (provider: 'google' | 'azure') => {
    if (!membership || !selectedInvitation) {
      toast.error('Please select a workspace first.');
      return;
    }

    // Persist invitation context so we can auto-accept on return
    savePendingOAuthInvite(membership.id, selectedInvitation.workspaceName);

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: window.location.origin,
          skipBrowserRedirect: false,
        },
      });

      if (error) {
        console.error('OAuth error:', error);
        clearPendingOAuthInvite();
        toast.error(`Failed to connect ${provider === 'azure' ? 'Microsoft' : 'Google'}. Please try again.`);
      }
    } catch (err) {
      console.error('OAuth exception:', err);
      clearPendingOAuthInvite();
      toast.error('Something went wrong. Please try again.');
    }
  };

  const handleAccept = async () => {
    if (!membership || !selectedInvitation) {
      toast.error('Please select a workspace to continue.');
      return;
    }

    // Password is required when using the manual accept path
    if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setPasswordError(null);
    await completeAccess(membership, password);
  };

  // Shared left-panel wrapper for the split-screen layout
  const renderLeftPanel = (children: React.ReactNode) => (
    <div className="h-screen grid lg:grid-cols-2 overflow-hidden">
      <div className="flex items-center justify-center bg-[#F2F0EF] p-8 overflow-y-auto">
        <div className="w-full max-w-sm">
          <div className="flex justify-center mb-8">
            <img src={productRadarLogo} alt="Product Radar" className="h-12" />
          </div>
          {children}
        </div>
      </div>
      <MarketingPanel />
    </div>
  );

  if (loading || accepting || autoAcceptPending) {
    return renderLeftPanel(
      <div className="flex flex-col items-center gap-4 py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#245f73]" />
        <p className="text-muted-foreground">
          {loading
            ? 'Verifying invitation...'
            : autoAcceptPending
              ? 'Connecting your account...'
              : 'Accepting invitation...'}
        </p>
      </div>
    );
  }

  if (error) {
    return renderLeftPanel(
      <>
        <h1 className="text-2xl font-semibold text-center mb-2 text-red-700">Invitation Issue</h1>

        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>

        <p className="text-sm text-muted-foreground text-center mb-6">
          If this keeps happening, ask your workspace administrator to resend the invitation or contact support.
        </p>

        <Button className="w-full h-11" onClick={() => window.location.reload()}>
          Try again
        </Button>
      </>
    );
  }

  // Main invitation UI (single or multiple)
  return renderLeftPanel(
    <>
      <h1 className="text-2xl font-semibold text-center mb-2">Accept Invitation</h1>
      <p className="text-sm text-muted-foreground text-center mb-8">
        Join your workspace by signing in with Google, Microsoft, or setting a password.
      </p>

      {/* Workspace selection (shown first when multiple invitations) */}
      {pendingInvitations.length > 1 && (
        <div className="space-y-2 mb-6">
          <h3 className="text-sm font-medium text-muted-foreground">Select a workspace</h3>
          <div className="space-y-2">
            {pendingInvitations.map((invitation) => {
              const membershipData = invitation.membership as WorkspaceMembership;
              const expired = isInvitationExpired(membershipData);
              const isSelected = selectedInvitation?.membership.id === invitation.membership.id;

              return (
                <Button
                  key={invitation.membership.id}
                  variant={isSelected ? 'default' : 'outline'}
                  className={`w-full justify-start h-auto py-4 ${!isSelected ? 'bg-white' : ''}`}
                  onClick={() => handleSelectInvitation(invitation)}
                  disabled={expired}
                >
                  <div className="text-left">
                    <div className="font-medium">{invitation.workspaceName}</div>
                    <div className={`text-sm capitalize ${isSelected ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                      {expired ? 'Expired' : invitation.membership.role}
                    </div>
                  </div>
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {/* Single invitation: show workspace name */}
      {pendingInvitations.length === 1 && selectedInvitation && (
        <div className="text-center mb-6">
          <p className="text-base font-medium text-foreground bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg px-4 py-3">
            You've been invited to join{' '}
            <span className="font-semibold text-green-800 dark:text-green-200">{selectedInvitation.workspaceName}</span>{' '}
            as a <span className="capitalize font-semibold">{selectedInvitation.membership.role}</span>.
          </p>
        </div>
      )}

      {/* OAuth buttons */}
      <div className="space-y-3 mb-6">
        <Button
          type="button"
          variant="outline"
          className="w-full h-11 text-sm font-normal bg-white"
          onClick={() => handleOAuthSignIn('google')}
          disabled={!membership}
        >
          <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </Button>
        <Button
          type="button"
          variant="outline"
          className="w-full h-11 text-sm font-normal bg-white"
          onClick={() => handleOAuthSignIn('azure')}
          disabled={!membership}
        >
          <svg className="mr-2 h-4 w-4" viewBox="0 0 23 23">
            <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
            <path fill="#f35325" d="M1 1h10v10H1z"/>
            <path fill="#81bc06" d="M12 1h10v10H12z"/>
            <path fill="#05a6f0" d="M1 12h10v10H1z"/>
            <path fill="#ffba08" d="M12 12h10v10H12z"/>
          </svg>
          Continue with Microsoft
        </Button>
      </div>

      {/* Divider */}
      <div className="relative my-6">
        <Separator />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#F2F0EF] px-3 text-xs text-muted-foreground">
          or create a password
        </span>
      </div>

      {/* Password creation */}
      <div className="space-y-4 mb-6">
        <div className="space-y-1.5">
          <Label htmlFor="password" className="text-sm">Password</Label>
          <Input
            id="password"
            type="password"
            placeholder="Enter a secure password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={6}
            className="h-11 bg-white"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirmPassword" className="text-sm">Confirm password</Label>
          <Input
            id="confirmPassword"
            type="password"
            placeholder="Re-enter password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            minLength={6}
            className="h-11 bg-white"
          />
        </div>
        {passwordError && (
          <p className="text-sm text-red-600">{passwordError}</p>
        )}
      </div>

      <Button
        className="w-full h-11"
        onClick={handleAccept}
        disabled={!membership || accepting}
      >
        {accepting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Accepting invitation...
          </>
        ) : (
          'Set password & accept invitation'
        )}
      </Button>
    </>
  );
}

